import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useUnit } from 'effector-react';
import { $user } from 'models/user';
import EmployeesPage from '@/pages/Employees/EmployeesPage';
import StoreSelectionPage from '@/pages/Stores/StoreSelectionPage';
import StoreConfirmationPage from '@/pages/Stores/StoreConfirmationPage';
import Layout from '@/pages/Layout';
import Login from '@/components/Login';
import NotFound from '@/components/NotFound/NotFound';
import { GroupsEnum } from '@/types/users';
import { normalizeGroup } from '@/utils/groups';
import { ProtectedRoute, RoleRoute, StoreRoute } from '@/routes/RouteGuards';
import { employeeRoutes, ShiftsPage } from '@/routes/employeeRoutes';
import { manualRoutes } from '@/routes/manualRoutes';
import { outsourcingRoutes } from '@/routes/outsourcingRoutes';
import { GeolocationRoute } from '@/routes/GeolocationRoute';

const QRCodePage = React.lazy(() => import('@/pages/QRCode/QRCodePage'));

const LandingPage = () => {
  const user = useUnit($user);
  const groups = new Set((user?.groups ?? []).map(normalizeGroup));
  if (groups.has(normalizeGroup(GroupsEnum.CLIENT))) {
    return (
      <RoleRoute groups={[GroupsEnum.CLIENT]} mobileOnly>
        <ShiftsPage />
      </RoleRoute>
    );
  }
  if (groups.has(normalizeGroup(GroupsEnum.GEN_QRCODE))) return <Navigate to="/store" replace />;
  if (groups.has(normalizeGroup(GroupsEnum.EMPLOYEE_LIST))) {
    return <Navigate to="/employees" replace />;
  }
  if (groups.has(normalizeGroup(GroupsEnum.MANUAL_REG))) return <Navigate to="/manual" replace />;
  if (groups.has(normalizeGroup(GroupsEnum.OUTSOURCE)))
    return <Navigate to="/outsourcing" replace />;
  return <NotFound />;
};

const AppRouter = () => {
  const { initialized } = useKeycloak();

  if (!initialized) {
    return <div>Загрузка...</div>;
  }

  return (
    <React.Suspense
      fallback={<div className="p-8 text-center text-gray-500">Загрузка раздела...</div>}
    >
      <Routes>
        <Route
          element={
            <ProtectedRoute>
              <GeolocationRoute>
                <StoreRoute>
                  <Layout />
                </StoreRoute>
              </GeolocationRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<LandingPage />} />
          <Route
            path="employees"
            element={
              <RoleRoute groups={[GroupsEnum.EMPLOYEE_LIST]}>
                <EmployeesPage />
              </RoleRoute>
            }
          />
          {employeeRoutes}
          <Route
            path="store"
            element={
              <RoleRoute groups={[GroupsEnum.GEN_QRCODE]}>
                <QRCodePage />
              </RoleRoute>
            }
          />
          <Route path="qrcode" element={<Navigate to="/store" replace />} />
          {manualRoutes}
          {outsourcingRoutes}
          <Route path="section" element={<StoreSelectionPage />} />
          <Route path="section/confirm/:storeId" element={<StoreConfirmationPage />} />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </React.Suspense>
  );
};

export default AppRouter;
